// ASKING /mcc's SECTION ENDPOINTS FOR AN ANSWER.
//
// They no longer compute one on the request. A manufacturing line's best score is a full solve plus a full
// scoring pass, and the listing asks for around forty of them; every one of those used to hold a Puma thread
// open for as long as it took. They are computed on the manufacturing_cost_calculator queue now
// (Frank::SectionAnswer), so an endpoint answers either the answer or `{ status: 'processing' }`, and the
// asker asks again until it lands.
//
// The URL never changes, which is the point: every caller keeps its own address, and so does everything keyed
// by it -- the response cache, its IndexedDB half, the "have we already scored this line" checks.

// How long to wait before asking again.
//
// SHORT, and it barely grows. A section's scores are computed one after another off a single working
// (Frank::SectionAnswerJob), so they land roughly every 130ms and the cards fill in as they do -- at which
// cadence a poll delay is pure added latency per card, and the 400ms-doubling-to-2s this started with turned a
// steady fill into laggy bursts, the last card waiting up to two seconds after its answer was ready.
//
// It still grows a little, so that something genuinely stuck (a worker killed mid-job holds its claim for
// PROCESSING_TTL, five minutes) is not asked four times a second for the whole deadline.
export const FIRST_POLL_DELAY_MILLISECONDS = 250;
export const MAXIMUM_POLL_DELAY_MILLISECONDS = 1000;
const POLL_BACKOFF = 1.15;

// When to stop believing an answer is coming. A worker killed mid-job writes nothing, and its claim on the work
// lasts five minutes server-side (Frank::SectionAnswer::PROCESSING_TTL). Set to that SAME five minutes on purpose:
// a round waits the whole lifetime a claim can have before it gives up, so it never gives up on -- nor asks the
// caller to drop and re-queue afresh (refresh) -- work that could still be computing. A real answer is measured in
// tens of seconds, so anything still unanswered at five minutes is a claim nobody is working on, not a slow one.
// Giving up then hands the caller its own recovery: the best-score path asks again in further rounds
// (SCORING_PATIENCE_MILLISECONDS in ManufacturingLineLists), the listing reports.
export const POLL_DEADLINE_MILLISECONDS = 300000;

// Add refresh=1 to a URL, keeping whatever query string it already has -- what a retry sends to drop a stuck
// claim (Frank::SectionAnswer#call). Shared with the best-score poll so the two build the flag the same way.
export const withRefresh = (url) => `${url}${url.includes('?') ? '&' : '?'}refresh=1`;

// The answer is not coming: the server failed to compute it, or refused the request. Distinct from a network or
// parse error, which the callers treat differently -- see fetchJsonCached, which reports this one as "no answer"
// and lets everything else propagate.
//
// `permanent` is WHETHER ASKING AGAIN COULD EVER HELP, so a caller that recovers by asking again knows when not
// to bother. A 4xx is the request itself being refused -- an expired session, an ask the endpoint does not take
// -- and repeating it verbatim can only be refused again; a failed computation or a server that said nothing is
// not permanent, and the next ask often lands. The same rule the best-score poll marks its own failures with
// (sectionBestScorePolling), in the same word, because the two are the same question.
export class SectionAnswerUnavailableError extends Error {
  constructor(message, { permanent = false } = {}) {
    super(message);
    this.name = 'SectionAnswerUnavailableError';
    this.permanent = permanent;
  }
}

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve();
  }, milliseconds);

  // An abort has to cut the WAIT as well as the request, or a superseded batch goes on ticking for its whole
  // backoff before it notices -- and a listing the viewer cancelled would keep asking for answers nobody wants.
  function onAbort() {
    clearTimeout(timer);
    reject(signal.reason instanceof Error ? signal.reason : new DOMException('Aborted', 'AbortError'));
  }

  if (signal?.aborted) { onAbort(); return; }
  signal?.addEventListener('abort', onAbort, { once: true });
});

// One section answer. Resolves with the completed body; rejects with SectionAnswerUnavailableError when the
// server says it failed, when it answers non-OK, or when the answer never arrives.
//
// `refresh` asks the server, on the FIRST poll only, to drop a claim that is stuck so the work is queued afresh --
// what a deliberate retry sends (the "Ara" button, or a round that timed out at POLL_DEADLINE). One-shot because
// sending it on every poll would delete the fresh claim the previous poll just made and re-queue the work each
// time, so it would never settle.
//
// `now` is injectable so the deadline can be exercised without waiting five minutes for it.
export const fetchSectionAnswer = (url, { signal, refresh = false, now = () => Date.now() } = {}) => {
  const startedAt = now();
  let delay = FIRST_POLL_DELAY_MILLISECONDS;
  let refreshNextAsk = refresh;

  const ask = () => {
    const askUrl = refreshNextAsk ? withRefresh(url) : url;
    refreshNextAsk = false;

    return fetch(askUrl, { signal }).then((response) => {
      // A non-OK response is a FAILURE, not an answer. Reading it as one is what once made a cut request look
      // like a manufacturing line that cannot be manufactured.
      if (!response.ok) {
        throw new SectionAnswerUnavailableError(
          `section answer responded ${response.status}`,
          { permanent: response.status >= 400 && response.status < 500 }
        );
      }

      return response.json();
    }).then((data) => {
      if (data?.status === 'failed') {
        throw new SectionAnswerUnavailableError(data.error || 'section answer failed');
      }
      if (data?.status !== 'processing') { return data; }

      if (now() - startedAt >= POLL_DEADLINE_MILLISECONDS) {
        throw new SectionAnswerUnavailableError('section answer did not arrive');
      }

      return wait(delay, signal).then(() => {
        delay = Math.min(delay * POLL_BACKOFF, MAXIMUM_POLL_DELAY_MILLISECONDS);
        return ask();
      });
    });
  };

  return ask();
};
